import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";

import { MOSQUE } from "@/config/constants";
import { db } from "@/config/firebase";
import { firestoreErrorCode } from "@/lib/firestoreErrors";
import { logger } from "@/lib/logger";

import type {
  ContributionSettings,
  EidSettings,
  HijriSettings,
  JummahSettings,
  MosqueProfile,
  PrayerSettings,
  RamadanSettings,
} from "./types";

const settingsDoc = (id: string) => doc(db, "settings", id);

// Jama Masjid Dariyapur's committee-set schedule. Admin can edit via
// /admin/prayer. There is no astronomical calculation — these are the values
// users will see.
export const DEFAULT_PRAYER_SETTINGS: PrayerSettings = {
  fajr: { azaan: "04:00", jamaah: "04:30" },
  dhuhr: { azaan: "13:00", jamaah: "13:30" },
  asr: { azaan: "16:31", jamaah: "16:46" },
  maghrib: { azaan: "18:33", jamaah: "18:33" },
  isha: { azaan: "20:00", jamaah: "20:30" },
};

export const DEFAULT_HIJRI_SETTINGS: HijriSettings = { offset: 0 };

export const DEFAULT_MOSQUE_PROFILE: MosqueProfile = {
  name: MOSQUE.name,
  village: MOSQUE.village,
  district: MOSQUE.district,
  country: MOSQUE.country,
};

export const DEFAULT_CONTRIBUTION_SETTINGS: ContributionSettings = {
  isPublished: false,
  placeholderMessage:
    "Contribution details will be updated after Mashwara. Jazak Allah Khair for your patience.",
};

// Fixed Jama Masjid Dariyapur Friday schedule (admin can edit via /admin/special-salah).
export const DEFAULT_JUMMAH_SETTINGS: JummahSettings = {
  azaanTime: "12:30",
  khutbahTime: "13:00",
  jamaahTime: "13:15",
};

export const DEFAULT_EID_SETTINGS: EidSettings = {
  isPublished: false,
};

export const DEFAULT_RAMADAN_SETTINGS: RamadanSettings = {
  sehriBufferMinutes: 0,
  iftarBufferMinutes: 0,
};

async function readOr<T>(id: string, fallback: T): Promise<T> {
  try {
    const snap = await getDoc(settingsDoc(id));
    if (snap.exists()) return { ...fallback, ...(snap.data() as object) } as T;
    return fallback;
  } catch (err) {
    logger.warn(`settings.${id} read failed; using default`, {
      code: firestoreErrorCode(err),
      message: err instanceof Error ? err.message : String(err),
    });
    return fallback;
  }
}

export const getPrayerSettings = () => readOr("prayer", DEFAULT_PRAYER_SETTINGS);
export const getHijriSettings = () => readOr("hijri", DEFAULT_HIJRI_SETTINGS);
export const getMosqueProfile = () => readOr("mosque", DEFAULT_MOSQUE_PROFILE);
export const getContributionSettings = () =>
  readOr("contribution", DEFAULT_CONTRIBUTION_SETTINGS);
export const getJummahSettings = () => readOr("jummah", DEFAULT_JUMMAH_SETTINGS);
export const getEidSettings = () => readOr("eid", DEFAULT_EID_SETTINGS);
export const getRamadanSettings = () => readOr("ramadan", DEFAULT_RAMADAN_SETTINGS);

function watch<T>(id: string, fallback: T, cb: (value: T) => void): () => void {
  return onSnapshot(
    settingsDoc(id),
    (snap) => {
      if (snap.exists()) cb({ ...fallback, ...(snap.data() as object) } as T);
      else cb(fallback);
    },
    (err) => {
      logger.warn(`settings.${id} subscribe failed; using default`, {
        code: firestoreErrorCode(err),
        message: err instanceof Error ? err.message : String(err),
      });
      cb(fallback);
    },
  );
}

export const watchPrayerSettings = (cb: (v: PrayerSettings) => void) =>
  watch("prayer", DEFAULT_PRAYER_SETTINGS, cb);
export const watchHijriSettings = (cb: (v: HijriSettings) => void) =>
  watch("hijri", DEFAULT_HIJRI_SETTINGS, cb);
export const watchMosqueProfile = (cb: (v: MosqueProfile) => void) =>
  watch("mosque", DEFAULT_MOSQUE_PROFILE, cb);
export const watchContributionSettings = (cb: (v: ContributionSettings) => void) =>
  watch("contribution", DEFAULT_CONTRIBUTION_SETTINGS, cb);
export const watchJummahSettings = (cb: (v: JummahSettings) => void) =>
  watch("jummah", DEFAULT_JUMMAH_SETTINGS, cb);
export const watchEidSettings = (cb: (v: EidSettings) => void) =>
  watch("eid", DEFAULT_EID_SETTINGS, cb);
export const watchRamadanSettings = (cb: (v: RamadanSettings) => void) =>
  watch("ramadan", DEFAULT_RAMADAN_SETTINGS, cb);

async function write<T extends object>(id: string, value: T): Promise<void> {
  await setDoc(
    settingsDoc(id),
    { ...value, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

/**
 * Prayer settings replace (not merge) so any legacy fields from the old
 * adhan-era schema (`method`, `madhab`, `azaanOverrides`, `offsetsMinutes`,
 * `latitude`, `longitude`) get cleared out the first time an admin saves
 * under the new simplified schema.
 */
export async function savePrayerSettings(v: PrayerSettings): Promise<void> {
  await setDoc(settingsDoc("prayer"), { ...v, updatedAt: serverTimestamp() });
}

export const saveHijriSettings = (v: HijriSettings) => write("hijri", v);
export const saveMosqueProfile = (v: MosqueProfile) => write("mosque", v);
export const saveContributionSettings = (v: ContributionSettings) => write("contribution", v);
export const saveJummahSettings = (v: JummahSettings) => write("jummah", v);
export const saveEidSettings = (v: EidSettings) => write("eid", v);
export const saveRamadanSettings = (v: RamadanSettings) => write("ramadan", v);
